import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Loader2, Copy, User, Key, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Party } from '@/app/types/party';
import { createClientUser, resetClientPin, ClientUserResponse, ResetPinResponse } from '@/app/services/partyService';

interface ClientUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  party: Party | null;
  mode: 'create' | 'reset';
}

const ClientUserDialog: React.FC<ClientUserDialogProps> = ({
  open,
  onOpenChange,
  party,
  mode,
}) => {
  const queryClient = useQueryClient();
  const [copiedField, setCopiedField] = useState<'username' | 'pin' | null>(null);
  const [credentials, setCredentials] = useState<{
    username: string;
    pin: string;
  } | null>(null);

  // Create client user mutation
  const createMutation = useMutation({
    mutationFn: (partyId: number) => createClientUser(partyId),
    onSuccess: (data: ClientUserResponse) => {
      setCredentials({
        username: data.username,
        pin: data.pin,
      });
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      toast.success('Client user created successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to create client user';
      toast.error(message);
      if (!error.response?.data?.detail?.includes('Please share')) {
        onOpenChange(false);
      }
    },
  });

  // Reset PIN mutation
  const resetMutation = useMutation({
    mutationFn: (partyId: number) => resetClientPin(partyId),
    onSuccess: (data: ResetPinResponse) => {
      setCredentials({
        username: party?.phone || '',
        pin: data.new_pin,
      });
      toast.success('PIN reset successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to reset PIN';
      toast.error(message);
      if (!error.response?.data?.detail?.includes('Please share')) {
        onOpenChange(false);
      }
    },
  });

  const handleAction = () => {
    if (!party) return;

    if (mode === 'create') {
      createMutation.mutate(party.id);
    } else {
      resetMutation.mutate(party.id);
    }
  };

  const handleCopy = (field: 'username' | 'pin', value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success(`${field === 'username' ? 'Username' : 'PIN'} copied to clipboard`);
  };

  const handleClose = () => {
    setCredentials(null);
    setCopiedField(null);
    onOpenChange(false);
  };

  const isLoading = createMutation.isPending || resetMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Client User' : 'Reset Client PIN'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? `Create a client user account for ${party?.name}`
              : `Reset the client PIN for ${party?.name}`}
          </DialogDescription>
        </DialogHeader>

        {!credentials ? (
          <>
            <div className="py-4">
              <Alert>
                <AlertDescription>
                  {mode === 'create'
                    ? 'This will create a new client user account with a 4-digit PIN.'
                    : 'This will generate a new 4-digit PIN for the existing client user.'}
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleAction} disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === 'create' ? 'Create Client User' : 'Reset PIN'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <Alert className="border-success/30 bg-success/5">
                <CheckCircle className="h-4 w-4 text-success" />
                <AlertDescription className="text-success">
                  {mode === 'create' ? 'Client user created successfully!' : 'PIN reset successfully!'}
                  Please share these credentials with the client.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Username</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={credentials.username}
                        readOnly
                        className="pl-10 font-mono"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy('username', credentials.username)}
                    >
                      {copiedField === 'username' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">PIN</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 relative">
                      <Key className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={credentials.pin}
                        readOnly
                        className="pl-10 font-mono text-lg tracking-wider"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy('pin', credentials.pin)}
                    >
                      {copiedField === 'pin' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Alert className="border-warning/30 bg-warning/5">
                <AlertDescription className="text-warning text-sm">
                  <strong>Important:</strong> Store these credentials safely. The PIN will not be visible again after closing this dialog.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientUserDialog;